import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX,
  AlertTriangle, CheckCircle, XCircle, Clock,
  FileText, Users, Lock, Eye, Key, Server,
  RefreshCw, Download, Plus, Trash2
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compliance Score Gauge
const ComplianceGauge = ({ score }) => {
  const getScoreColor = (s) => {
    if (s >= 90) return 'text-emerald-600';
    if (s >= 75) return 'text-yellow-600';
    if (s >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreLabel = (s) => {
    if (s >= 90) return 'Compliant';
    if (s >= 75) return 'Needs Attention';
    if (s >= 50) return 'At Risk';
    return 'Critical';
  };

  return (
    <Card className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center">
          <div className="relative">
            <svg className="w-40 h-40 transform -rotate-90">
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke="currentColor"
                strokeWidth="12"
                fill="transparent"
                className="text-slate-200 dark:text-slate-700"
              />
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke="currentColor"
                strokeWidth="12"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 70}`}
                strokeDashoffset={`${2 * Math.PI * 70 * (1 - score / 100)}`}
                className={getScoreColor(score)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-4xl font-bold ${getScoreColor(score)}`}>{score}</span>
              <span className="text-sm text-muted-foreground">/ 100</span>
            </div>
          </div>
          <Badge className={`mt-4 ${score >= 90 ? 'bg-emerald-100 text-emerald-700' : score >= 75 ? 'bg-yellow-100 text-yellow-700' : score >= 50 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
            {getScoreLabel(score)}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Security Alert Card
const SecurityAlertCard = ({ alert, onAcknowledge, onResolve }) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'CRITICAL': return { color: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50 dark:bg-red-900/20' };
      case 'HIGH': return { color: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50 dark:bg-orange-900/20' };
      case 'MEDIUM': return { color: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50 dark:bg-yellow-900/20' };
      default: return { color: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50 dark:bg-blue-900/20' };
    }
  };

  const config = getSeverityConfig(alert.severity);

  return (
    <div className={`p-4 rounded-xl ${config.bg} border border-slate-200 dark:border-slate-700`}>
      <div className="flex items-start gap-3">
        <div className={`h-3 w-3 rounded-full mt-1.5 ${config.color}`} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className={`font-semibold ${config.text}`}>{alert.type.replace(/_/g, ' ')}</span>
            <Badge variant="outline" className="text-xs">{alert.severity}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{JSON.stringify(alert.details)}</p>
          <p className="text-xs text-muted-foreground mt-2">{new Date(alert.timestamp).toLocaleString()}</p>
          {alert.status === 'OPEN' && (
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => onAcknowledge(alert.id)}>
                Acknowledge
              </Button>
              <Button size="sm" onClick={() => onResolve(alert.id)}>
                Resolve
              </Button>
            </div>
          )}
          {alert.status === 'RESOLVED' && (
            <Badge className="mt-2 bg-emerald-100 text-emerald-700">Resolved</Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default function HIPAACompliance() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [baas, setBaas] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [mfaCompliance, setMfaCompliance] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // BAA Form
  const [newBaa, setNewBaa] = useState({
    vendor: '',
    service: '',
    signed_date: '',
    expiration_date: '',
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [reportRes, alertsRes, baasRes, mfaRes] = await Promise.all([
        fetch(`${API_URL}/api/hipaa/compliance-report`, { headers }),
        fetch(`${API_URL}/api/hipaa/security-alerts?status=OPEN`, { headers }),
        fetch(`${API_URL}/api/hipaa/baas`, { headers }),
        fetch(`${API_URL}/api/hipaa/mfa-compliance`, { headers })
      ]);

      if (reportRes.ok) setReport(await reportRes.json());
      if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || []);
      if (baasRes.ok) setBaas((await baasRes.json()).baas || []);
      if (mfaRes.ok) setMfaCompliance(await mfaRes.json());
    } catch (error) {
      console.error('Failed to fetch HIPAA data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hipaa/audit-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAuditLogs((await res.json()).logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (activeTab === 'audit') fetchAuditLogs();
  }, [activeTab]);

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/hipaa/security-alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/hipaa/security-alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolution: 'Manually resolved by admin' })
      });
      if (res.ok) {
        toast.success('Alert resolved');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleAddBaa = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams(newBaa);
      const res = await fetch(`${API_URL}/api/hipaa/baas?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('BAA recorded');
        setNewBaa({ vendor: '', service: '', signed_date: '', expiration_date: '', notes: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add BAA');
    }
  };

  const handleDeleteBaa = async (baaId) => {
    try {
      const res = await fetch(`${API_URL}/api/hipaa/baas/${baaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('BAA deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete BAA');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hipaa-compliance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            HIPAA Compliance Center
          </h1>
          <p className="text-muted-foreground mt-1">Monitor and manage HIPAA Privacy & Security compliance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Compliance Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ComplianceGauge score={report?.overall_compliance_score || 0} />
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Open Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {alerts.length > 0 ? (
                <ShieldAlert className="h-8 w-8 text-red-500" />
              ) : (
                <ShieldCheck className="h-8 w-8 text-emerald-500" />
              )}
              <div>
                <p className="text-2xl font-bold">{alerts.length}</p>
                <p className="text-xs text-muted-foreground">Requires attention</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">MFA Adoption</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Lock className={`h-8 w-8 ${(mfaCompliance?.compliance_rate || 0) >= 100 ? 'text-emerald-500' : 'text-yellow-500'}`} />
              <div>
                <p className="text-2xl font-bold">{mfaCompliance?.compliance_rate || 0}%</p>
                <p className="text-xs text-muted-foreground">{mfaCompliance?.mfa_enabled || 0} / {mfaCompliance?.total_users || 0} users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">BAA Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <FileText className={`h-8 w-8 ${report?.baa_status?.compliant ? 'text-emerald-500' : 'text-red-500'}`} />
              <div>
                <p className="text-2xl font-bold">{report?.baa_status?.compliant_baas?.length || 0}/4</p>
                <p className="text-xs text-muted-foreground">Agreements in place</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations Alert */}
      {report?.recommendations?.length > 0 && report.recommendations[0] !== 'System is compliant. Continue monitoring for any changes.' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Action Required</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1">
              {report.recommendations.map((rec, i) => (
                <li key={i} className="text-sm">{rec}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="baas">BAAs</TabsTrigger>
          <TabsTrigger value="mfa">MFA</TabsTrigger>
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Score Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Compliance Score Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(report?.scores || {}).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{value.toFixed(0)}%</span>
                    </div>
                    <Progress value={value} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Audit Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Audit Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <p className="text-2xl font-bold">{report?.audit_stats?.total_logs || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Logs</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <p className="text-2xl font-bold">{report?.audit_stats?.today_logs || 0}</p>
                    <p className="text-sm text-muted-foreground">Today</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <p className="text-2xl font-bold">{report?.audit_stats?.phi_accesses || 0}</p>
                    <p className="text-sm text-muted-foreground">PHI Accesses</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <p className="text-2xl font-bold">{report?.security_alerts?.open || 0}</p>
                    <p className="text-sm text-muted-foreground">Open Alerts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Alerts</CardTitle>
              <CardDescription>Active security alerts requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8">
                  <ShieldCheck className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <p className="font-semibold text-emerald-600">No Open Alerts</p>
                  <p className="text-sm text-muted-foreground">All security alerts have been addressed</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <SecurityAlertCard
                      key={alert.id}
                      alert={alert}
                      onAcknowledge={handleAcknowledgeAlert}
                      onResolve={handleResolveAlert}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="baas" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Business Associate Agreements</CardTitle>
              <CardDescription>Track BAAs with vendors handling PHI</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Required BAAs Status */}
              <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <h4 className="font-semibold mb-3">Required BAAs Status</h4>
                <div className="space-y-2">
                  {['MongoDB Atlas', 'Emergent AI', 'Email Provider', 'Cloud Hosting'].map((vendor) => {
                    const hasBaa = baas.some(b => b.vendor === vendor);
                    return (
                      <div key={vendor} className="flex items-center justify-between">
                        <span className="text-sm">{vendor}</span>
                        {hasBaa ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="h-3 w-3 mr-1" /> Verified
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" /> Missing
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add BAA Form */}
              <form onSubmit={handleAddBaa} className="mb-6 p-4 border rounded-xl">
                <h4 className="font-semibold mb-3">Add New BAA</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vendor">Vendor Name</Label>
                    <Input
                      id="vendor"
                      value={newBaa.vendor}
                      onChange={(e) => setNewBaa({ ...newBaa, vendor: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="service">Service Type</Label>
                    <Input
                      id="service"
                      value={newBaa.service}
                      onChange={(e) => setNewBaa({ ...newBaa, service: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="signed_date">Signed Date</Label>
                    <Input
                      id="signed_date"
                      type="date"
                      value={newBaa.signed_date}
                      onChange={(e) => setNewBaa({ ...newBaa, signed_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="expiration_date">Expiration Date</Label>
                    <Input
                      id="expiration_date"
                      type="date"
                      value={newBaa.expiration_date}
                      onChange={(e) => setNewBaa({ ...newBaa, expiration_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={newBaa.notes}
                    onChange={(e) => setNewBaa({ ...newBaa, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <Button type="submit" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Add BAA
                </Button>
              </form>

              {/* BAA List */}
              {baas.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold">Recorded BAAs</h4>
                  {baas.map((baa) => (
                    <div key={baa.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div>
                        <p className="font-medium">{baa.vendor}</p>
                        <p className="text-sm text-muted-foreground">{baa.service} • Signed: {baa.signed_date}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteBaa(baa.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mfa" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Multi-Factor Authentication Compliance</CardTitle>
              <CardDescription>MFA is required for all users accessing PHI</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl text-center">
                  <p className="text-3xl font-bold">{mfaCompliance?.total_users || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-center">
                  <p className="text-3xl font-bold text-emerald-600">{mfaCompliance?.mfa_enabled || 0}</p>
                  <p className="text-sm text-muted-foreground">MFA Enabled</p>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl text-center">
                  <p className="text-3xl font-bold text-red-600">{mfaCompliance?.non_compliant_users?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Non-Compliant</p>
                </div>
              </div>

              {mfaCompliance?.non_compliant_users?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    Users Without MFA
                  </h4>
                  <div className="space-y-2">
                    {mfaCompliance.non_compliant_users.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <Badge variant="outline">{user.role}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Audit Logs</CardTitle>
              <CardDescription>System activity and PHI access logs</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {auditLogs.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                      <div className={`h-2 w-2 rounded-full mt-2 ${log.phi_accessed ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{log.user_email}</span>
                          {log.phi_accessed && (
                            <Badge variant="outline" className="text-xs">PHI</Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground truncate">{log.action}</p>
                        <p className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleString()} • {log.ip_address}</p>
                      </div>
                      <Badge variant={log.status_code < 400 ? 'secondary' : 'destructive'}>
                        {log.status_code}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
